import { computed, effect, Injectable, signal } from '@angular/core';
import { DateTime, Duration } from 'luxon';
import { NextObserver, Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import _ from 'underscore';

export interface BusyActivity {
    id: string;
}

interface BusyActivityItem extends BusyActivity {
    timeOutOn?: DateTime;
}

const busyActivityTimeOutInSeconds = Duration.fromObject({ seconds: 15 }).as('seconds');

@Injectable({
    providedIn: 'root',
})
export class BffApiBusyService {
    public readonly isBusy = computed<boolean>(() => this.busyActivities().length > 0);

    private checkTimeOut$ = timer(0, 1000);
    private stopCheckTimeOut = new Subject<void>();

    private busyActivities = signal<BusyActivityItem[]>([]);

    constructor() {
        effect(() => {
            if (!this.isBusy()) {
                this.stopCheckTimeOut.next();
                this.stopCheckTimeOut.complete();
            } else {
                this.stopCheckTimeOut = new Subject<void>();
                this.checkTimeOut$.pipe(takeUntil(this.stopCheckTimeOut)).subscribe(this.onCheckTimeOut);
            }
        });
    }

    public add(busyActivity: BusyActivity): void {
        const busyActivityExists = this.busyActivities().some((activity) => activity.id === busyActivity.id);
        const newBusyActivityTimeOutOn = DateTime.now().plus({ seconds: busyActivityTimeOutInSeconds });

        if (busyActivityExists) {
            this.busyActivities.update((activities) =>
                activities.map((activity) => (activity.id === busyActivity.id ? { ...activity, timeOutOn: newBusyActivityTimeOutOn } : activity)),
            );
        } else {
            this.busyActivities.update((activities) => [...activities, { id: busyActivity.id, timeOutOn: newBusyActivityTimeOutOn }]);
        }
    }

    public remove(busyActivityId: string) {
        this.busyActivities.update((activities) => activities.filter((activity) => activity.id !== busyActivityId));
    }

    public clear(): void {
        this.busyActivities.set([]);
    }

    private onCheckTimeOut: NextObserver<number> = {
        next: () => {
            const now = DateTime.now();
            const expiredActivities = _.filter(this.busyActivities(), (busyActivity) => {
                return busyActivity.timeOutOn != undefined && now > busyActivity.timeOutOn;
            });
            for (const activity of expiredActivities) {
                this.busyActivities.update((activities) => activities.filter((busyActivity) => busyActivity !== activity));
            }
        },
    };
}
